import { SimpleUserRoleSchema } from "./user-schemas";

export type AnalysisNoteActionType =
  | "NOTE"
  | "STATE_UPDATE"
  | "RELEASE_LOCK"
  | "CHANGED_FLOWN_AT"
  | "TASK"
  | "ROLE_ASSIGNED"
  | "READY_FOR_REVIEW"
  | "DEADLINE_CHANGED"
  | "CHANGED_PRODUCT_PACKAGES"
  | "CHANGED_DRONE"
  | "CHANGED_KEY_FIGURES"
  | "EVALUATION_ERROR"
  | "EVALUATION_INFO";

export const ANALYSIS_NOTE_ACTION_TYPES: readonly AnalysisNoteActionType[] = [
  "NOTE",
  "STATE_UPDATE",
  "RELEASE_LOCK",
  "CHANGED_FLOWN_AT",
  "TASK",
  "ROLE_ASSIGNED",
  "DEADLINE_CHANGED",
  "CHANGED_PRODUCT_PACKAGES",
  "CHANGED_DRONE",
  "CHANGED_KEY_FIGURES",
  "EVALUATION_ERROR",
  "EVALUATION_INFO",
];

export interface AnalysisNoteAttachmentSchema {
  id: string;
  file_name: string;
  /** Presigned S3 download/preview url. */
  url: string | null;
}

export interface AnalysisNoteReactionSchema {
  id: string;
  /** One of the fixed, supported reaction emojis (see REACTION_EMOJIS). */
  emoji: string;
  /** User that left the reaction. */
  user: SimpleUserRoleSchema | null;
}

export interface AnalysisNoteSchema {
  id: string;
  action_type: AnalysisNoteActionType;
  description: string;
  /** Evaluation batch (EB) number this entry relates to, or null for plant-wide / untagged entries. */
  eval_batch_num: number | null;
  created_at: string;
  /** Set once the author edits the note; null while unchanged since creation. */
  edited_at: string | null;
  /**
   * Encoded id of the note this entry replies to, if any.
   * Null when not a reply, or when the parent was deleted (resolved live — no quote snapshot).
   */
  parent_note_id: string | null;
  /** Author of the entry. Null for automatically logged system events. */
  user: SimpleUserRoleSchema | null;
  attachments: AnalysisNoteAttachmentSchema[];
  mentioned_users: SimpleUserRoleSchema[];
  reactions: AnalysisNoteReactionSchema[];
}

/** Whether an analysis has user written notes the current user has not read yet. */
export interface NoteUnreadStatus {
  has_unread: boolean;
  /** Whether any of the unread notes tag (mention) the current user. */
  has_unread_mention: boolean;
}

/** Map of encoded analysis id -> unread status, as returned by the unread-status endpoint. */
export type NoteUnreadStatusByAnalysis = Record<string, NoteUnreadStatus>;

/**
 * Effective subscription of the current user to an analysis' notes thread.
 * `mandatory` is true while the user is the analysis' assigned F2C or Ops person —
 * they stay subscribed and cannot unsubscribe until the role is reassigned.
 */
export interface AnalysisNoteSubscriptionStatus {
  subscribed: boolean;
  mandatory: boolean;
}
